import { IRouteProp } from "@/interfaces/common/IRoute";
import ProspectService from "@/services/prospect/prospectService";
import store from "@/store";
import DOMPurify from "dompurify";
import { isEqual } from "lodash";
import get from "lodash/get";
import cloneDeep from "lodash/cloneDeep";
import {
  COLOR_QUALITY,
  DEFAULT_AVATAR_COLOR,
  MFAStatus,
  PREFIX_URL,
  arrayDataRiskAssessmentDefault,
  localStorageKey,
  statusCode,
  QUALITY_ASSESSMENT_TYPE,
} from "./constants";
import { homeRoute } from "@/services/apiRoutes/routes";
import { tlDomain } from "./validationRules";
import { Sha256 } from "@aws-crypto/sha256-js";
import {
  IAssessmentChecklistItem,
  IAssessmentDetail,
  IAssessmentQuestionTable,
} from "@/interfaces/assessments/IAssessmentDetails";
import router from "@/router";
import { getOrgSubsPlan } from "@/services/subscriptions";
import { computed, ref } from "vue";
import dayjs from "dayjs";
import ProgressAnimation from "../assets/animation/progress_light.json";
import permissionMatrixData from "@/permissions_matrix.json";
import { IMultiSelectDropdown } from "@/interfaces/common/IDropDown";
import { remoOrEditveStep } from "@/services/recommend";
import { convertUTCTimeToLocalTime } from "./dateTimeUtils";
import { getOrgDetails } from "@/services/org";

export const getDifferences = (objA: object, objB: object) => {
  if (Array.isArray(objA) && Array.isArray(objB)) {
    return objA
      .map((itemA) => getDifferences(itemA, objB[objB.length - 1]))
      .filter(Boolean);
  }

  if (
    typeof objA === "object" &&
    objA !== null &&
    typeof objB === "object" &&
    objB !== null
  ) {
    const diff = {};
    for (const key in objA) {
      if (!(key in objB) || key === "id" || key.includes("Id")) {
        diff[key] = objA[key];
      } else {
        const valueDiff = getDifferences(objA[key], objB[key]);
        if (
          valueDiff !== undefined &&
          (typeof valueDiff !== "object" || Object.keys(valueDiff).length > 0)
        ) {
          diff[key] = valueDiff;
        }
      }
    }
    return Object.keys(diff).length > 1 ? diff : undefined;
  }

  return objA !== objB && (objA || objB) ? objA : undefined;
};

export const getNextDay = (date: Date) => {
  if (date) {
    const now = new Date();
    const differenceInMilliseconds = date.getTime() - now.getTime();
    const differenceInMinutes = Math.ceil(
      differenceInMilliseconds / (60 * 1000),
    );
    const differenceInHours = Math.ceil(differenceInMinutes / 60);
    const differenceInDays = Math.ceil(differenceInHours / 24);

    if (differenceInMinutes > 0 && differenceInMinutes < 60) {
      return `${getComponentUiText(
        "app-scanning-schedule-next-scan-in-1",
      )} ${differenceInMinutes} ${
        differenceInMinutes > 1
          ? getComponentUiText("app-notification-minutes-1")
          : getComponentUiText("app-notification-minute-1")
      }`;
    }

    if (differenceInHours > 0 && differenceInHours < 24) {
      return `${getComponentUiText(
        "app-scanning-schedule-next-scan-in-1",
      )} ${differenceInHours} ${
        differenceInHours > 1
          ? getComponentUiText("app-notification-hours-1")
          : getComponentUiText("app-notification-hour-1")
      }`;
    }

    if (differenceInDays > 0 && differenceInDays < 31) {
      return `${getComponentUiText(
        "app-scanning-schedule-next-scan-in-1",
      )} ${differenceInDays} ${
        differenceInDays > 1
          ? getComponentUiText("app-notification-days-1")
          : getComponentUiText("app-notification-day-1")
      }`;
    }

    if (differenceInDays >= 31 || differenceInMilliseconds < 0) {
      return convertUTCTimeToLocalTime(date.toISOString());
    }
  } else {
    return "";
  }
};

export const getPasswordDefaultVendorAttachment = (providerId: any) => {
  return `CyberSystem_${providerId}_password`;
};

export const searchQuestion = (data: any[], searchValue = "") => {
  if (!data || !searchValue) return data;

  const listData: any[] = [];

  const matchesSearch = (item: any) =>
    item?.checklistItemName?.toLowerCase().includes(lowerSearch);
  const addUniqueItem = (item: any) => {
    if (
      item &&
      listData.findIndex((e) => e.checklistItemId == item.checklistItemId) == -1
    ) {
      listData.push(item);
    }
  };
  const getParentGroupings = (item: any) =>
    [1, 2, 3, 4]
      .map((level) => item[`checklistItemIdParentGrouping${level}`])
      .filter(Boolean);
  const isValidGrouping = (item: any, data: any[]) =>
    getParentGroupings(item).some((id) =>
      data.some((heading) => heading.checklistItemId == id),
    );

  const lowerSearch = searchValue.toLowerCase();
  const listHeadingSearch = data.filter(
    (item) => matchesSearch(item) && item.checklistItemIsHeading,
  );
  const listQuestionSearch = data.filter(
    (item) => matchesSearch(item) && !item.checklistItemIsHeading,
  );

  if (
    getComponentUiText("app-component-assessments-input-question-other-1")
      .toLowerCase()
      .includes(lowerSearch)
  ) {
    data
      .filter(
        (item) => !item.checklistItemIsHeading && !isValidGrouping(item, data),
      )
      .forEach(addUniqueItem);
  }

  listHeadingSearch.forEach((item) => {
    addUniqueItem(item);
    getParentGroupings(item).forEach((id) =>
      addUniqueItem(data.find((child) => child?.checklistItemId == id)),
    );
  });

  data.forEach((item) => {
    if (
      listData.findIndex(
        (parent) =>
          parent.checklistItemId == item.checklistItemIdParentGrouping1,
      ) != -1
    ) {
      addUniqueItem(item);
    }
  });

  listQuestionSearch.forEach((item) => {
    addUniqueItem(item);
    getParentGroupings(item).forEach((id) =>
      addUniqueItem(data.find((child) => child?.checklistItemId == id)),
    );
  });

  return listData;
};

export const formatVendorData = (dataBeforeSort: any) => {
  const data =
    dataBeforeSort?.sort(
      (a: any, b: any) => a.checklistItemOrder - b.checklistItemOrder,
    ) ?? [];
  const listHeading = data.filter(
    (item: any) =>
      item.checklistItemIsHeading &&
      item.checklistItemIdParentGrouping1 === null,
  );

  function getChildren(item: any, level: number) {
    if (!item.checklistItemHasChildren) return [];
    const children = data
      .filter(
        (child: any) =>
          child[`checklistItemIdParentGrouping${level}`] ==
            item.checklistItemId &&
          (level === 4 ||
            child[`checklistItemIdParentGrouping${level + 1}`] == null),
      )
      .map((child: any) => {
        child.checklistItemChildren = getChildren(child, level + 1);
        return child;
      });
    return children;
  }

  listHeading.forEach((item: any) => {
    if (!item.checklistItemHasChildren) return;
    item.checklistItemChildren = getChildren(item, 1);
  });

  const otherItems = data.filter((item: any) => {
    if (item.checklistItemIsHeading) return false;
    if (item.checklistItemIdParentGrouping1 === null) return true;

    const isExitGroupIds1 = data.some(
      (heading: any) =>
        heading.checklistItemId == item.checklistItemIdParentGrouping1,
    );

    const isExitGroupIds2 =
      item.checklistItemIdParentGrouping2 === null ||
      data.some(
        (heading: any) =>
          heading.checklistItemId == item.checklistItemIdParentGrouping2,
      );

    const isExitGroupIds3 =
      item.checklistItemIdParentGrouping3 === null ||
      data.some(
        (heading: any) =>
          heading.checklistItemId == item.checklistItemIdParentGrouping3,
      );

    const isExitGroupIds4 =
      item.checklistItemIdParentGrouping4 === null ||
      data.some(
        (heading: any) =>
          heading.checklistItemId == item.checklistItemIdParentGrouping4,
      );

    return !(
      isExitGroupIds1 &&
      isExitGroupIds2 &&
      isExitGroupIds3 &&
      isExitGroupIds4
    );
  });

  if (otherItems.length) {
    listHeading.push({
      itemMasterId: null,
      checklistItemName: "Other",
      checklistItemChildren: otherItems,
      checklistItemIsHeading: true,
    });
  }

  return listHeading;
};

export const validDateAction = (
  itemId: any,
  checklistGroups: any,
  dataForecast: any,
) => {
  const checklistGroupDateUpdated = checklistGroups.find(
    (item) => item.checklistGroupId === itemId,
  )?.checklistGroupDateUpdated;

  const forecastDate = dataForecast.find(
    (item) => item.id === itemId,
  ).forecastDate;

  if (!forecastDate || !checklistGroupDateUpdated) {
    return null;
  }

  return new Date(forecastDate).getTime() >
    new Date(checklistGroupDateUpdated).getTime()
    ? null
    : false;
};

export const getProgressAnimation = () => {
  return ProgressAnimation;
};

export const getParent = (
  item: any,
  question: IAssessmentChecklistItem | any,
) => {
  let output: any;
  const loopParent = (data: any) => {
    if (output) {
      return output;
    }
    (data.checklistChildren || []).forEach((child: any) => {
      if (child?.checklistItemOrder === item?.checklistItemOrder) {
        output = data;
      } else {
        output = loopParent(child);
      }
    });
    return output;
  };
  output = loopParent(question);
  return output;
};

export const getChecklistGroupData = (
  arrayQuestion: IAssessmentQuestionTable[] | any,
  arrayId: number[],
) => {
  const output: IAssessmentQuestionTable[] = [];
  for (let i = 0, n = arrayQuestion.length, m = arrayId.length; i < n; ++i) {
    if (output.length === m) {
      return output;
    }
    const question = arrayQuestion[i];
    if (arrayId.includes(question.checklistItemId)) {
      output.push({ ...question, checklistChildren: undefined });
    }
    if (question.checklistChildren?.length) {
      const data = getChecklistGroupData(
        question.checklistChildren as IAssessmentQuestionTable[],
        arrayId,
      );
      output.push(...data);
    }
  }
  return output;
};

export const checkIsHavePermisstion = async (function_code: string) => {
  const currentUser = JSON.parse(
    localStorage.getItem(localStorageKey.currentUserData) || "",
  );
  if (!currentUser) return false;

  const role = computed(() => store.state.Org.listRolesByUser);
  if (!role.value.length) {
    await store.dispatch("Org/getRoleByUserId", currentUser.userId);
  }

  const maximunPermData = computed(
    () => store.state.Org.maximunPermissionLevelData,
  );
  if (!maximunPermData.value.permissionLevelId) {
    await store.dispatch("Org/getPermissionByRoleId");
  }

  const homeOrgPlanDetails = ref(
    store.state.Org.orgsList.find((item: any) => item.isHome)?.planDetails,
  );
  if (!homeOrgPlanDetails.value?.planId) {
    try {
      const homeOrgDetail = await getOrgDetails(currentUser.orgId);
      homeOrgPlanDetails.value = homeOrgDetail.data.data.planDetails;
    } catch (err) {
      homeOrgPlanDetails.value = { planLevel: 0 };
    }
  }

  const permissionToFunctionCode = permissionMatrixData.find(
    (item) => item.function_code === function_code,
  );

  const isHavePermisstion =
    permissionToFunctionCode &&
    maximunPermData.value.permissionLevelValue >=
      permissionToFunctionCode?.min_role_perm_level &&
    homeOrgPlanDetails.value.planLevel >=
      permissionToFunctionCode?.min_plan_level;

  return isHavePermisstion;
};

const convertDataFlatToTree = (
  data: Array<{ id: string; parentId: string | null }>,
) => {
  const tree = {};

  data.forEach((obj) => {
    if (obj.parentId !== null) {
      if (tree[obj.parentId]) {
        tree[obj.parentId].childData.push(obj);
      } else {
        tree[obj.parentId] = { childData: [obj] };
      }
    } else {
      if (tree[obj.id]) {
        tree[obj.id] = { ...tree[obj.id], ...obj };
      } else {
        tree[obj.id] = { ...obj, childData: [] };
      }
    }
  });

  return tree;
};

export const countLeafNodes = (categories) => {
  let count = 0;

  const traverse = (node) => {
    if (!node.childrenCategory || node.childrenCategory.length === 0) {
      count++;
    } else {
      node.childrenCategory.forEach(traverse);
    }
  };

  categories.forEach(traverse);

  return count;
};

export const getShortChecklistName = (name: string) => {
  return name?.length > 20 ? `${name?.slice(0, 17)}...` : name;
};

const IsEmailValid = (email: string) => {
  const emailRegex = /[A-Z0-9._+]+@[A-Z0-9.-]+\.[A-Z]{2,}/gim;
  if (email) {
    return emailRegex.test(email);
  }
};

const IsDomainNameValid = (name: string) => {
  const domainRegex = /[A-Z0-9.-]+\.[A-Z]{2,}/gim;
  if (name) {
    return domainRegex.test(name);
  }
};

const IsDomainNameValidCustom = (name: string) => {
  let isValid = true;
  if (name && name.length > 1) {
    const dnSplits = name.split(".");
    if (dnSplits && dnSplits.length > 1) {
      for (const ds of dnSplits) {
        if (!ds || ds.length < 2) {
          isValid = false;
        }
      }
    }
  }
  return isValid;
};

const getQuestionsMatchedListKey = (listId: string) => {
  let listKey = "";
  if (listId && listId != null) {
    switch (listId) {
      case "lifecycle":
        listKey = "lifecycle";
        break;
      case "market":
        listKey = "markets";
        break;
      case "deliv_products":
        listKey = "delivery_product_types";
        break;
      case "deliv_services":
        listKey = "delivery_service_types";
        break;
      case "ext_standards":
        listKey = "external_standards";
        break;
      case "group_main":
        listKey = "group_mains";
        break;
      case "industry":
        listKey = "sector";
        break;
      case "role":
        listKey = "functional_roles";
        break;
      case "staff_count":
        listKey = "staff_count_band";
        break;
      case "assets":
        listKey = "asset_types";
        break;
      case "services":
        listKey = "service_types";
        break;
      case "controls":
        listKey = "control_types";
        break;

      default:
        listKey = listId;
        break;
    }
  }
  return listKey;
};

const getLabelCustom = (val: string) => {
  let finalVal = "";
  finalVal = val.replace("_", " ");
  finalVal = finalVal.charAt(0).toUpperCase() + finalVal.slice(1);
  return finalVal;
};

const getListDataKeyAtLevel = (name: string, currLevel: string) => {
  let currLevelDataKey = "";
  let levelsList: Array<string> = [];
  switch (name) {
    case "industry":
      levelsList = ["", "industry_group", "industry"];
      break;
    case "assets":
      levelsList = [
        "",
        "asset_types",
        "asset_types",
        "asset_types",
        "asset_types",
        "asset_types",
      ];
      break;
    case "services":
      levelsList = ["", "service_types", "service_types", "service_types"];
      break;
    case "controls":
      levelsList = ["", "control_types", "control_types", "control_types"];
      break;
  }

  if (currLevel && levelsList && levelsList.length > 0) {
    const ln = currLevel;

    if (ln && ln !== undefined && parseInt(ln) && levelsList[parseInt(ln)]) {
      currLevelDataKey = levelsList[parseInt(ln)];
    }
  }

  return currLevelDataKey;
};

const getTextWidth = (element: string, font_size: number, page: string) => {
  const text = document.createElement("div");

  document.body.appendChild(text);

  if (page === "lang") {
    text.style.fontFamily = "normal";
    text.style.fontSize = "20px";
    text.style.fontWeight = "500";
    text.style.lineHeight = "19px";
    text.style.height = "auto";
    text.style.width = "auto";
    text.style.position = "absolute";
    text.style.whiteSpace = "normal";
  } else if (page != "revenue") {
    text.style.fontFamily = "poppins";
    text.style.fontSize = font_size + "px";
    text.style.fontWeight = "500";
    text.style.height = "auto";
    text.style.width = "auto";
    text.style.lineHeight = "48px";
    text.style.position = "absolute";
    text.style.whiteSpace = "normal";
  } else {
    text.style.fontFamily = "normal";
    text.style.fontSize = font_size + "px";
    text.style.fontWeight = "500";
    text.style.height = "auto";
    text.style.width = "auto";
    text.style.lineHeight = "30px";
    text.style.position = "absolute";
  }
  text.innerHTML = element;
  const width = Math.ceil(text?.clientWidth);
  const formattedWidth = width;
  document.body.removeChild(text);

  return formattedWidth;
};

export const getLatestScan = (item) => {
  let latestScanIndex = 0;
  let latestScanTime = 0;
  item.scanReportRuns.forEach((item, index) => {
    const currentTime = new Date(item.startedAt || 0).getTime();
    if (latestScanTime < currentTime) {
      latestScanTime = currentTime;
      latestScanIndex = index;
    }
  });
  return { latestScanIndex, latestScanTime };
};

const dropDownFitContent = (
  list: any,
  primaryPlaceholder: string,
  type: string,
  isCustom = false,
) => {
  let maxL = 0;
  let elements = "";
  let font_size = 0;
  let ex_M_count = 0;
  const char_limits = {
    max_char_large: 50,
    max_char: 35,
    max_char_tab: 35,
    max_char_mobile: 10,
  };
  if (type == "singleselect" || type == "lang") {
    list.forEach((element: any) => {
      element.name += "";
      const M_count =
        element.name.split("M").length -
        1 +
        element.name.split("m").length -
        1 +
        element.name.split("W").length -
        1 +
        element.name.split("w").length -
        1;

      if (element.name.length > maxL) {
        ex_M_count = M_count;

        maxL = element.name.length;
        elements = element.name;
      } else if (element.name.length == maxL) {
        if (M_count > ex_M_count) {
          maxL = element.name.length;
          elements = element.name;
          ex_M_count = M_count;
        }
      }
    });
    if (primaryPlaceholder.length >= maxL) {
      const M_count =
        primaryPlaceholder.split("M").length -
        1 +
        primaryPlaceholder.split("m").length -
        1 +
        primaryPlaceholder.split("W").length -
        1 +
        primaryPlaceholder.split("w").length -
        1;
      if (M_count >= ex_M_count) {
        elements = primaryPlaceholder;
      }
    }
  } else if (type == "multiselect") {
    elements = primaryPlaceholder;
  } else if (type == "revenue") {
    list.forEach((element: any) => {
      element.amountRange += "";
      if (element.amountRange.length > maxL) {
        maxL = element.amountRange.length;
        elements = element.amountRange;
      }
    });
    if (primaryPlaceholder.length >= maxL) {
      elements = primaryPlaceholder;
    }
  } else {
    list.forEach((element: string) => {
      if (element.length > maxL) {
        maxL = element.length;
        elements = element;
      }
    });
    if (primaryPlaceholder.length >= maxL) {
      elements = primaryPlaceholder;
    }
  }

  if (type != "revenue") {
    if (window.innerWidth >= 3840) {
      if (isCustom && type == "multiLevel") {
        font_size = 36;
      } else {
        font_size = 64;
      }
      elements = elements.substring(0, char_limits["max_char_large"]);
    } else if (window.innerWidth >= 1025) {
      if (isCustom && type == "multiLevel") {
        font_size = 18;
      } else {
        font_size = 32;
      }
      window.innerWidth >= 2500
        ? elements.substring(0, char_limits["max_char_large"])
        : (elements = elements.substring(0, char_limits["max_char"]));
    } else if (window.innerWidth >= 768) {
      font_size = isCustom && type == "multiLevel" ? 18 : 32;

      elements = elements.substring(0, char_limits["max_char_tab"]);
      if (window.innerHeight <= 500) {
        elements = elements.substring(0, char_limits["max_char_mobile"]);
        font_size = 18;
      }
    } else {
      elements = elements.substring(0, char_limits["max_char_mobile"]);
      font_size = 18;
    }
  } else {
    if (window.innerWidth >= 2000) {
      font_size = 52;
    } else if (window.innerWidth >= 1025) {
      font_size = 22;
    } else if (window.innerWidth >= 768) {
      font_size = 22;

      if (window.innerHeight <= 500) {
        font_size = 18;
      }
    } else {
      font_size = 18;
    }
  }
  return getTextWidth(elements, font_size, type);
};

const getUiText = (
  id: string,
  languageText: any,
  defaultUiText: any,
  isArray: boolean,
) => {
  let count = 0;
  let isBreak = false;
  let text = "";

  while (!isBreak) {
    const result: any = Object.values(languageText).filter(
      (ele: any) => ele.id.toLowerCase() === id.toLowerCase(),
    );
    if (result && result[0]) {
      if (isArray) {
        text = result[0].text;
        isBreak = true;
      } else {
        text = result[0].text ? result[0].text[0] : "";
        isBreak = true;
      }
    } else {
      if (count == 1) {
        text = "not found !";
        isBreak = true;
      }
      count = 1;
      languageText = defaultUiText;
    }
  }
  return text;
};

const getProcessedUIText = (
  id: string,
  tags: any,
  languageText: any,
  defaultUiText: any,
) => {
  const text = getUiText(id, languageText, defaultUiText, false);
  let result: any = [];
  let nomralText = "";
  for (let i = 0; i < text.length; i++) {
    if (text[i] == "{") {
      nomralText !== "" && result.push(nomralText);
      nomralText = "";
      let identifier = "";
      for (; text[i] != "}"; i++) {
        if (
          text[i] != "#" &&
          text[i] != "}" &&
          text[i] != "{" &&
          text[i] != " "
        ) {
          identifier += text[i];
        }
      }
      let modifiedText = getUiText(
        identifier,
        languageText,
        defaultUiText,
        false,
      );
      if (tags[identifier]) {
        modifiedText =
          tags[identifier]["openTag"] +
          modifiedText +
          tags[identifier]["closeTag"];
      }
      result.push(modifiedText);
    } else {
      nomralText += text[i];
    }
  }
  result.push(nomralText);
  result = result.join("");
  return result;
};

const getV3Recaptcha = async () => {
  await new Promise((resolve) => setTimeout(resolve, 10));
  store.dispatch("User/setActiveCallApi", true);
  const prospectService = new ProspectService();
  return new Promise((resolve, reject) => {
    prospectService.getReCaptcha().then(
      (res: any) => {
        if (res && res.data) {
          const scriptCode = res.data.split("?render=");
          if (scriptCode && scriptCode.length > 1) {
            const keyFromCode = scriptCode[1].split(">");

            if (
              keyFromCode &&
              keyFromCode.length > 0 &&
              keyFromCode[0] !== ""
            ) {
              const finalKey = keyFromCode[0].replace('"', "");
              resolve(finalKey);
            }
          }
        }
      },
      (error: any) => {
        reject(error);
      },
    );
  });
};

const getUniqueID = () => {
  return Math.floor(Math.random() * Date.now()).toString();
};

const sanitizeHTML = (html: any) => {
  return DOMPurify.sanitize(html);
};
// validate both email and domain name
export const IsSpecialCharEmailValid = (val) => {
  if (val) {
    const validEmailRegex =
      /^[A-Za-z0-9.+]+(?:-[A-Za-z0-9.+]+)*@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/;
    return validEmailRegex.test(val);
  }
  return true;
};

export const IsEmailValidWhileTyping = (val) => {
  if (val) {
    const validEmailRegex =
      // eslint-disable-next-line no-useless-escape
      /^[a-zA-Z0-9](?!.*[+.\-]{2})[a-zA-Z0-9+.\-]*(@[a-zA-Z0-9-]*(\.[a-zA-Z]{0,4})*)?$/;
    const validEmailRegex2 = /^(?!.*[+.\-@]{2})/;
    return validEmailRegex.test(val) && validEmailRegex2.test(val);
  }
  return true;
};

export const IsSpecialCharEmailNameValidWhieTyping = (val) => {
  if (val) {
    const validEmailRegex = /^[a-zA-Z0-9.+-]+$/;
    return validEmailRegex.test(val);
  }
  return true;
};

// validate only email name
export const IsSpecialCharEmailNameValid = (val) => {
  if (val) {
    const validEmailRegex = /^[A-Za-z0-9.+]+(?:-[A-Za-z0-9.+]+)*$/;
    return validEmailRegex.test(val);
  }
  return true;
};

export const IsSpecialCharDomainValid = (val) => {
  if (val) {
    const validRegex =
      /^[A-Za-z0-9.]+([A-Za-z0-9.]*-[A-Za-z0-9.]+)*[A-Za-z0-9.]+$/;
    return validRegex.test(val);
  }
  return true;
};

export const IsPhoneNumber = (val) => {
  if (val) {
    const phoneNumberRegex = /^\+?(?:\d+(?:-?\s?\d+){6,})$/;
    return phoneNumberRegex.test(val);
  }
  return true;
};

export const invalidCharPhoneNumber = (val) => {
  if (val) {
    const phoneNumberRegex = /^[\d\s+-]*$/;
    return phoneNumberRegex.test(val);
  }
  return true;
};

export const isHeadCount = (val) => {
  if (val) {
    const headCountRegex = /^\d+$/u;
    return headCountRegex.test(val);
  }
  return true;
};

export const IsSpecialCharNameValid = (val) => {
  if (val) {
    const nameData: any = getFirstAndLastNameFromName(val);
    return nameData.firstName && nameData.lastName;
  }
  return true;
};

export const IsNumberBetween0And100Valid = (val) => {
  if (val) {
    const specialCharRegex = /^[0-9]$|^[0-9][0-9]$|^(100)$/gim;
    return specialCharRegex.test(val);
  }
  return true;
};

export const isUrlWebService = (val) => {
  if (val) {
    const specialCharRegex =
      /^((ftp|http|https):\/\/)?(www.)?(?!.*(ftp|http|https|www.))[a-zA-Z0-9_-]+(\.[a-zA-Z]+)+((\/)[\w#]+)*(\/\w+\?[a-zA-Z0-9_]+=\w+(&[a-zA-Z0-9_]+=\w+)*)?$/gm;
    return specialCharRegex.test(val);
  }
  return true;
};

export const hasInvalidDomainChars = (domain: string) => {
  if (!domain) return true;
  const allowedDomainCharRegex = /^[a-zA-Z0-9\-._~:/?#[\]@!$&'()*+,;=]+$/;
  return allowedDomainCharRegex.test(domain);
};

export const isUrlFacingWeb = (val) => {
  const domainUrl = isWrongHttpUrl(val);
  if (domainUrl === true) return false;
  const indexSubDomain = domainUrl.indexOf("/");
  if (indexSubDomain !== -1) {
    const domainPart = domainUrl.substring(0, indexSubDomain);
    // const subDomainPart = domainUrl.substring(indexSubDomain + 1);
    return tlDomain(domainPart);
  } else return tlDomain(domainUrl);
};

export const getDomainFromUrl = (val: string) => {
  const domainUrl = isWrongHttpUrl(val);
  if (domainUrl === true) return "";
  const indexSubDomain = domainUrl.indexOf("/");
  if (indexSubDomain !== -1) {
    const domainPart = domainUrl.substring(0, indexSubDomain);
    // const subDomainPart = domainUrl.substring(indexSubDomain + 1);
    return domainPart;
  } else return domainUrl;
};

export const isWrongHttpUrl = (input: string) => {
  const indexHttp = input.indexOf("://");
  if (indexHttp !== -1) {
    const httpPart = input.substring(0, indexHttp);
    return PREFIX_URL.includes(httpPart)
      ? input.substring(indexHttp + 3)
      : true;
  } else return input;
};

export const getComponentUiText = (
  id: string,
  isArray = false,
  data: Record<string, any> = {},
  isLink = false,
): string => {
  const languageSelectedText = store.state.LanguagePicker.languageSelectedText;
  const defaultUiText = store.state.LanguagePicker.defaultUiText;

  let uiText = getUiText(id, languageSelectedText, defaultUiText, isArray);

  if (isLink) {
    uiText = uiText.replace(
      "{##linkText##}",
      `<span target="_blank" class="tw-text-[#387dff] hover:tw-underline tw-cursor-pointer" id="upgrade-link">${data.linkText}</span>`,
    );
  } else {
    // Use Object.entries to loop through the data object and replace placeholders in the UI text
    Object.entries(data).forEach(([key, value]) => {
      while (uiText.includes(`{##${key}##}`)) {
        if (value?.css) {
          uiText = uiText.replace(
            `{##${key}##}`,
            `<span class="${value.css}">${value.value}</span>`,
          );
        } else {
          uiText = uiText.replace(`{##${key}##}`, value);
        }
      }
    });
  }

  return uiText;
};

/**
 * Compares two objects by checking if their key-value pairs are equal.
 *
 * This function iterates through the keys of both input objects and compares
 * the corresponding values for equality. If all key-value pairs are equal,
 * the function returns true. If any key-value pair is not equal, the function
 * returns false.
 *
 * @param {Object} currentObject - The first object to be compared.
 * @param {Object} originalObject - The second object to be compared.
 * @returns {boolean} Returns `true` if all key-value pairs are equal, `false` otherwise.
 *
 * @example
 * const obj1 = { a: 1, b: 2 };
 * const obj2 = { a: 1, b: 2 };
 * const result = compareObjects(obj1, obj2); // Returns true
 */
export const compareObjects = (
  currentObject: any,
  originalObject: any,
): boolean => {
  for (const k in currentObject) {
    if (!isEqual(originalObject[k], currentObject[k])) return false;
  }
  for (const k in originalObject) {
    if (!isEqual(originalObject[k], currentObject[k])) return false;
  }
  return true;
};

export const getFirstAndLastNameFromName = (fullName = ""): object => {
  let firstName = "";
  let lastName = "";
  fullName = fullName || "";
  const parts: any = fullName.trim().split(/\s/);

  if (parts.length > 1) {
    firstName = parts[0];
    lastName = parts[parts.length - 1];
  } else {
    lastName = parts[0];
  }

  return {
    firstName: firstName,
    lastName: lastName,
  };
};

const getDomainMessage = (email) => {
  return getComponentUiText(
    "app-settings-organisation-manage-access-modals-error-domain-already-access-1",
    false,
    { domain: email?.match(/@([a-zA-Z0-9.-]+)$/)?.[1] || "" },
  );
};

const base64toBlob = (base64Data: string, contentType: any) => {
  contentType = contentType || "";
  const sliceSize = 1024;
  const byteCharacters = atob(base64Data);
  const bytesLength = byteCharacters.length;
  const slicesCount = Math.ceil(bytesLength / sliceSize);
  const byteArrays = new Array(slicesCount);

  for (let sliceIndex = 0; sliceIndex < slicesCount; ++sliceIndex) {
    const begin = sliceIndex * sliceSize;
    const end = Math.min(begin + sliceSize, bytesLength);

    const bytes = new Array(end - begin);
    for (let offset = begin, i = 0; offset < end; ++i, ++offset) {
      bytes[i] = byteCharacters[offset].charCodeAt(0);
    }
    byteArrays[sliceIndex] = new Uint8Array(bytes);
  }
  return new Blob(byteArrays, { type: contentType });
};

const base64ToFile = (
  base64Data: string,
  filename: string,
  contentType: any,
) => {
  const blob = base64toBlob(base64Data, contentType);
  const file = new File([blob], filename, {
    type: contentType,
    lastModified: Date.now(),
  });
  return file;
};
const getProcessedRoute = (route: string, props: IRouteProp[]) => {
  let processedRoute = route;
  props.forEach(({ key, value }) => {
    processedRoute = processedRoute.replaceAll(key, value);
  });
  return processedRoute;
};

const searchFilter = (
  data: any,
  dataKey: string[],
  searchText: string,
  sortByAsc: boolean,
  sortKey: string,
  overrideText = "",
) => {
  let temp: any = [...data];
  if (searchText) {
    temp = data.filter((inp: any) => {
      const dataKeyValue = dataKey.map(
        (keyItem) => get(inp, keyItem, "") ?? "",
      );
      return dataKeyValue.some(
        (value) =>
          value.toLowerCase().includes(searchText.trim().toLowerCase()) ||
          (overrideText
            ? overrideText
                .toLowerCase()
                .includes(searchText.trim().toLowerCase())
            : getComponentUiText(`app-notification-${inp.type}-1`)
                .toLowerCase()
                .includes(searchText.trim().toLowerCase())),
      );
    });
  }
  const sortedTemp: any = temp.sort(
    (a: any, b: any) =>
      new Date(get(a, sortKey, "")).getTime() -
      new Date(get(b, sortKey, "")).getTime(),
  );
  return sortByAsc ? sortedTemp : sortedTemp.reverse();
};

export const getFullnamFromFirstAndLastName = (
  firstName = "",
  lastName = "",
): string => {
  return `${firstName ? firstName : ""} ${lastName ? lastName : ""}`;
};

const afterLoginActions = async (currentUser) => {
  store.dispatch("User/authenticateUser");

  // Check org plan detail
  const orgSubsPlan = await getOrgSubsPlan();
  const orgSubsPlanDetail = orgSubsPlan.data?.data;

  store.dispatch("Org/setCurrentOrgPlanDetails", orgSubsPlanDetail);
  store.dispatch(
    "Notification/setSystemActions",
    currentUser.systemActions || 0,
  );

  if (orgSubsPlanDetail && orgSubsPlanDetail.planId) {
    router.push(homeRoute.welcomePage);
  } else {
    // If current org plan is not set and current org different from user org, switch user to current org
    if (
      currentUser &&
      currentUser.orgId &&
      currentUser.orgId !== currentUser.currentOrgId
    ) {
      await store.dispatch("Org/switchOrg", {
        orgId: currentUser.orgId,
        isHomeOrg: true,
      });
      router.push(homeRoute.welcomePage);
    } else {
      router.push(homeRoute.homePage);
      return;
    }
  }

  store.dispatch("Notification/setLoadAfterLogin", true);
  store.dispatch("Navigation/setActiveHierarchy", [
    getComponentUiText("app-menu-home-1"),
    getComponentUiText("app-menu-submenu-home-dashboard-1"),
    "",
    "",
  ]);
  store.dispatch("Navigation/setPrimaryMenuIndex", 0);
};

const regexMatch = (regex: any, text: string, isGlobal = true): boolean => {
  const expression: RegExp = isGlobal
    ? new RegExp(regex, "g")
    : new RegExp(regex);
  return expression.test(text);
};

const isEnableMFA = (item) => {
  if (item.mfaEnabled === MFAStatus.NOT_REQUIRED) return true;
  return false;
};

const getISOLanguageCode = (languageCode: string) => {
  const languageCodeMap = {
    eng: "en",
    fre: "fr",
    chi: "zh",
  };
  return languageCodeMap[languageCode] || "en";
};

const formatBytes = (bytes, decimals = 2) => {
  if (!+bytes) return "0 Bytes";

  const k = 1024;
  const dm = decimals < 0 ? 0 : decimals;
  const sizes = ["Bytes", "KB", "MB", "GB", "TB", "PB", "EB", "ZB", "YB"];

  const i = Math.floor(Math.log(bytes) / Math.log(k));

  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(dm))} ${sizes[i]}`;
};

const downloadBase64File = (name, base64Data, contentType) => {
  const linkSource = base64Data.includes(";base64,")
    ? base64Data
    : `data:${contentType};base64,${base64Data}`;
  const downloadLink = document.createElement("a");
  downloadLink.href = linkSource;
  downloadLink.download = name;
  downloadLink.click();
};

const chars =
  "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789-_";

// Use a lookup table to find the index.
const lookup = new Uint8Array(256);
for (let i = 0; i < chars.length; i++) {
  lookup[chars.charCodeAt(i)] = i;
}

const encode = (arraybuffer) => {
  const bytes = new Uint8Array(arraybuffer),
    len = bytes.length;
  let i,
    base64url = "";

  for (i = 0; i < len; i += 3) {
    base64url += chars[bytes[i] >> 2];
    base64url += chars[((bytes[i] & 3) << 4) | (bytes[i + 1] >> 4)];
    base64url += chars[((bytes[i + 1] & 15) << 2) | (bytes[i + 2] >> 6)];
    base64url += chars[bytes[i + 2] & 63];
  }

  if (len % 3 === 2) {
    base64url = base64url.substring(0, base64url.length - 1);
  } else if (len % 3 === 1) {
    base64url = base64url.substring(0, base64url.length - 2);
  }

  return base64url;
};

const decode = (base64string) => {
  const bufferLength = base64string.length * 0.75,
    len = base64string.length;
  let i,
    p = 0,
    encoded1,
    encoded2,
    encoded3,
    encoded4;

  const bytes = new Uint8Array(bufferLength);

  for (i = 0; i < len; i += 4) {
    encoded1 = lookup[base64string.charCodeAt(i)];
    encoded2 = lookup[base64string.charCodeAt(i + 1)];
    encoded3 = lookup[base64string.charCodeAt(i + 2)];
    encoded4 = lookup[base64string.charCodeAt(i + 3)];

    bytes[p++] = (encoded1 << 2) | (encoded2 >> 4);
    bytes[p++] = ((encoded2 & 15) << 4) | (encoded3 >> 2);
    bytes[p++] = ((encoded3 & 3) << 6) | (encoded4 & 63);
  }

  return bytes.buffer;
};

export const removeNullProperties = (obj) => {
  if (obj.type === "input") {
    return;
  }
  for (const key in obj) {
    if (obj[key] === null) {
      delete obj[key];
    } else if (typeof obj[key] === "object") {
      removeNullProperties(obj[key]);
      if (Object.keys(obj[key]).length === 0) {
        delete obj[key];
      }
    }
  }
};

export const optionDay = (num) => {
  const objectsArray: any = [];
  for (let i = 1; i <= num; i++) {
    objectsArray.push({ id: i, name: i });
  }
  return objectsArray;
};

export const passwordHash = async (password, passwordIV) => {
  let resultHashPassword = "";
  const hash = new Sha256(passwordIV);
  hash.update(password);
  const result = await hash.digest();
  resultHashPassword = encode(result);

  return resultHashPassword;
};

export function mergeScanIssuesCounts(obj1, obj2) {
  const result = {
    total: 0,
    critical: 0,
    high: 0,
    med: 0,
    low: 0,
  };

  function addScans(arr) {
    arr.total && (result.total += arr.total);
    arr.critical && (result.critical += arr.critical);
    arr.high && (result.high += arr.high);
    arr.med && (result.med += arr.med);
    arr.low && (result.low += arr.low);
  }

  addScans(obj1);
  addScans(obj2);

  return result;
}

export function mergeScanCounts(arr1, arr2) {
  const result = {
    scansCountTotal: 0,
    scansCountCurrent: 0,
  };

  function addScans(arr) {
    arr.forEach((scan) => {
      result.scansCountTotal += scan.scansCountTotal;
      result.scansCountCurrent += scan.scansCountCurrent;
    });
  }

  addScans(arr1);
  addScans(arr2);

  return result;
}

export const getElapsedTime = (
  startedTime: string | any,
  elapsedTime: string | any,
) => {
  const now = dayjs(elapsedTime || new Date());
  const start = dayjs(startedTime);
  const seconds = now.diff(start) / 1000;
  const minutes = Math.floor(seconds / 60);
  if (minutes <= 1) {
    return getComponentUiText("app-component-scan-scanning-timer-a-minute-1");
  }
  if (minutes < 60) {
    const textSec = getComponentUiText("app-notification-second-1");
    const textSecs = getComponentUiText("app-notification-seconds-1");
    const textMinute = getComponentUiText("app-notification-minute-1");
    const textMinutes = getComponentUiText("app-notification-minutes-1");
    const sec = Math.floor(seconds - minutes * 60);
    return `${minutes} ${minutes > 1 ? textMinutes : textMinute}${
      sec > 0 ? `, ${sec} ${sec > 1 ? textSecs : textSec}` : ""
    } `;
  }
  const hours = Math.floor(minutes / 60);
  if (hours < 24) {
    const textMinute = getComponentUiText("app-notification-minute-1");
    const textMinutes = getComponentUiText("app-notification-minutes-1");
    const textHour = getComponentUiText("app-scanning-hour-1");
    const textHours = getComponentUiText("app-notification-hours-1");
    const min = Math.floor(minutes - hours * 60);
    return `${hours} ${hours > 1 ? textHours : textHour}${
      min > 0 ? `, ${min} ${min > 1 ? textMinutes : textMinute}` : ""
    }`;
  }
  const days = Math.floor(hours / 24);
  if (days < 30) {
    const textDay = getComponentUiText("app-scanning-day-1");
    const textDays = getComponentUiText("app-notification-days-1");
    const textHour = getComponentUiText("app-scanning-hour-1");
    const textHours = getComponentUiText("app-notification-hours-1");
    const hou = Math.floor(hours - days * 24);
    return `${days} ${days > 1 ? textDays : textDay}${
      hou > 0 ? `, ${hou} ${hou > 1 ? textHours : textHour}` : ""
    } `;
  }
  return getComponentUiText("app-component-scan-scanning-timer-over-month-1");
};

export function calculateTotalIssues(data) {
  const currentMonth = dayjs().format("M");
  let firstMonth;
  const monthTotals = {};
  for (let i = 0; i < 12; i++) {
    if (Number(currentMonth) - i < 1) {
      firstMonth = 12 + (Number(currentMonth) - i);
      monthTotals[12 + (Number(currentMonth) - i)] = null;
    } else {
      firstMonth = Number(currentMonth) - i;
      monthTotals[Number(currentMonth) - i] = null;
    }
  }
  // append all scanReportRuns
  const reportIssues: any = [];
  data.forEach((schedule: any) => {
    reportIssues.push(...schedule.scanReportRuns);
  });
  reportIssues.sort(
    (a, b) => new Date(a.startedAt).getTime() - new Date(b.startedAt).getTime(),
  );
  if (!reportIssues.length) return [];
  const lastMonthHasData = dayjs(
    reportIssues[reportIssues.length - 1].startedAt,
  ).format("M");
  data.forEach((schedule: any) => {
    const mostRecentSuccessRuns = schedule.scanReportRuns
      .filter((obj) => obj.runStatusId === "ok")
      .sort(
        (a, b) =>
          new Date(a.startedAt).getTime() - new Date(b.startedAt).getTime(),
      );
    if (!mostRecentSuccessRuns.length) {
      return;
    }
    // get record in 12 month pass as now
    const runInThisYear = mostRecentSuccessRuns.filter((run) =>
      dayjs(run.startedAt).isAfter(dayjs().subtract(12, "months")),
    );
    // get remain record
    const runInLastYear = mostRecentSuccessRuns.filter(
      (run) => !runInThisYear.includes(run),
    );

    // only get one record per month for each domain/webservice
    const report: any = [];
    let currentMonth = runInThisYear.length
      ? dayjs(runInThisYear[0].startedAt).format("M")
      : null;
    if (
      !currentMonth ||
      (currentMonth != `${firstMonth}` && runInLastYear.length)
    ) {
      runInThisYear.push({
        ...runInLastYear[runInLastYear.length - 1],
        startedAt: dayjs(runInLastYear[runInLastYear.length - 1].startedAt).set(
          "month",
          firstMonth - 1,
        ),
      });
      currentMonth = `${firstMonth}`;
    }
    runInThisYear.sort(
      (a, b) =>
        new Date(a.startedAt).getTime() - new Date(b.startedAt).getTime(),
    );
    runInThisYear.forEach((run) => {
      const month = dayjs(run.startedAt).format("M");
      if (month !== currentMonth) {
        report.push(run);
        currentMonth = month;
      } else {
        report[report.length ? report.length - 1 : 0] = run;
      }
    });
    // add fake data for moth that has no data - will be the value of the nearest month in the past
    const lastReports = [...report];
    let isFakeLastMonth = false;
    report.forEach((rp: any, index) => {
      const month = dayjs(cloneDeep(rp.startedAt)).format("M");
      let nextMonth;
      if (index === report.length - 1) {
        if (lastMonthHasData === month) {
          return;
        } else {
          nextMonth = lastMonthHasData;
          isFakeLastMonth = true;
        }
      } else {
        nextMonth = dayjs(report[index + 1].startedAt).format("M");
      }
      if (
        Number(nextMonth) - Number(month) != 1 &&
        Number(nextMonth) - Number(month) != -11
      ) {
        const nextData = new Date(cloneDeep(rp.startedAt));
        // duplicate the data for the next month clone from rp and add moth + 1
        nextData.setMonth(nextData.getMonth() + 1);
        while (dayjs(nextData).format("M") !== nextMonth) {
          const newRp = { ...rp, startedAt: cloneDeep(nextData) };
          lastReports.push(newRp);
          nextData.setMonth(nextData.getMonth() + 1);
        }
      }
    });
    if (isFakeLastMonth) {
      const newRp = {
        ...lastReports[lastReports.length - 1],
        startedAt: cloneDeep(
          dayjs(lastReports[lastReports.length - 1].startedAt).set(
            "month",
            Number(lastMonthHasData) - 1,
          ),
        ),
      };
      lastReports.push(newRp);
    }
    lastReports.sort(
      (a, b) =>
        new Date(a.startedAt).getTime() - new Date(b.startedAt).getTime(),
    );

    lastReports.forEach((report: any) => {
      // only get 12 month pass as now
      if (dayjs(report.startedAt).isBefore(dayjs().subtract(12, "months")))
        return;
      const month = dayjs(report.startedAt).format("M");

      monthTotals[month] += Number(report.total);
    });
  });
  return Object.keys(monthTotals).map((monthYear) => ({
    month: monthYear,
    total: monthTotals[monthYear],
  }));
}

export const fillterAll = (arr, value) => {
  const searchLowerCase = value.toLowerCase();
  if (!searchLowerCase.length) return arr;

  return arr.filter((item) => {
    if (item.checklistItemName?.toLowerCase().includes(searchLowerCase))
      return true;
    if (item.orderNumber?.toLowerCase().includes(searchLowerCase)) return true;
  });
};

export const calculatePercentile = (arr: number[], percentile: number) => {
  try {
    if (percentile < 0 || percentile > 100) {
      throw new Error("Percentile must be between 0 and 100");
    }

    arr.sort((a, b) => a - b);

    const index = (percentile / 100) * (arr.length - 1);
    const lowerIndex = Math.floor(index);
    const upperIndex = Math.ceil(index);

    if (lowerIndex === upperIndex) {
      return arr[lowerIndex];
    }

    const fraction = index - lowerIndex;
    return arr[lowerIndex] + (arr[upperIndex] - arr[lowerIndex]) * fraction;
  } catch (error) {
    return 0;
  }
};

export const getTagRelativeRisk = (value: number, percentileValue: any) => {
  if (value > percentileValue.percentile_90) return statusCode.highest;
  if (value > percentileValue.percentile_75) return statusCode.high;
  if (value > percentileValue.percentile_33) return statusCode.moderate;
  return null;
};

export const resultData = (inputData) => {
  let data: IAssessmentChecklistItem[] = [];
  if (!inputData.showModalAssessment) return;
  if (
    inputData.initalValueSearch === "" ||
    (inputData.initalValueSearch !== "" && inputData.modelValue === "")
  ) {
    data = fillterAll(inputData.getDataChecklistChildAll, inputData.modelValue);
  } else {
    inputData.searchListQuestion.forEach((item: any) => {
      if (item.checklistChildren) {
        data = fillterAll(
          [...data, ...item.checklistChildren],
          inputData.modelValue,
        );
      }
    });
  }

  return data;
};

export const getDefaultBackgroundColor = (name) => {
  if (!name) return DEFAULT_AVATAR_COLOR[0];
  const firstLetter = name[0].toUpperCase();
  if (firstLetter >= "A" && firstLetter <= "F") {
    return DEFAULT_AVATAR_COLOR[0];
  } else if (firstLetter >= "G" && firstLetter <= "L") {
    return DEFAULT_AVATAR_COLOR[1];
  } else if (firstLetter >= "M" && firstLetter <= "R") {
    return DEFAULT_AVATAR_COLOR[2];
  } else if (firstLetter >= "S" && firstLetter <= "Z") {
    return DEFAULT_AVATAR_COLOR[3];
  } else {
    return DEFAULT_AVATAR_COLOR[0];
  }
};

export const downloadCSV = (res, name) => {
  const blob = new Blob([res.data], { type: "text/csv" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = name;
  a.click();
  URL.revokeObjectURL(url);
};

export const getImageData = async (imageDetails: any) => {
  const filename: string = imageDetails.fileName;
  const fileType: string = imageDetails.fileType;
  const base64FileData: string = imageDetails.file;
  return base64ToFile(base64FileData, filename, fileType);
};

export const processingRiskValue = async (dataMetric: any) => {
  if (!dataMetric.length) return [];
  const data = cloneDeep(arrayDataRiskAssessmentDefault);
  await dataMetric.forEach((item: any) => {
    item.riskDetails.forEach(async (riskDetail: any) => {
      const impactValue = await convertRiskValue(
        riskDetail.riskImpactLevelValue,
      );
      const likelihoodValue = await convertRiskValue(
        riskDetail.riskProbabilityLevelValue,
      );
      if (
        impactValue >= 0 &&
        impactValue <= 4 &&
        likelihoodValue >= 0 &&
        likelihoodValue <= 4
      ) {
        const value = data[4 - impactValue].gridItem[likelihoodValue]?.number;
        if (value !== null || value !== undefined || !isNaN(value)) {
          data[4 - impactValue] &&
            data[4 - impactValue].gridItem[likelihoodValue] &&
            data[4 - impactValue].gridItem[likelihoodValue].number++;
        }
      }
    });
  });
  return data;
};

export const convertRiskValue = async (riskValue: any) => {
  switch (riskValue) {
    case 4:
      return 3;
    case 8:
      return 4;
    default:
      return riskValue;
  }
};

export const countElementsFromTree = (trees) => {
  let count = 0;
  trees.forEach((node) => {
    if (node.checklistChildren && node.checklistChildren.length > 0) {
      count += countElementsFromTree(node.checklistChildren);
    } else {
      count += 1;
    }
  });
  return count;
};

export const convertQuality = (
  quality,
  isCompleted = false,
  type = QUALITY_ASSESSMENT_TYPE.IMPORTANCE,
) => {
  switch (quality) {
    case "l":
      return isCompleted
        ? [COLOR_QUALITY.GREY_DARK, COLOR_QUALITY.GREY, COLOR_QUALITY.GREY]
        : [COLOR_QUALITY.GREEN, COLOR_QUALITY.GREY, COLOR_QUALITY.GREY];
    case "m":
      return type === QUALITY_ASSESSMENT_TYPE.IMPORTANCE
        ? isCompleted
          ? [
              COLOR_QUALITY.GREY_DARK,
              COLOR_QUALITY.GREY_DARK,
              COLOR_QUALITY.GREY,
            ]
          : [COLOR_QUALITY.GREEN, COLOR_QUALITY.GREEN, COLOR_QUALITY.GREY]
        : isCompleted
          ? [
              COLOR_QUALITY.GREY_DARK,
              COLOR_QUALITY.GREY_DARK,
              COLOR_QUALITY.GREY,
            ]
          : [COLOR_QUALITY.YELLOW, COLOR_QUALITY.YELLOW, COLOR_QUALITY.GREY];
    case "h":
    case "vh":
      return type === QUALITY_ASSESSMENT_TYPE.IMPORTANCE
        ? isCompleted
          ? [
              COLOR_QUALITY.GREY_DARK,
              COLOR_QUALITY.GREY_DARK,
              COLOR_QUALITY.GREY_DARK,
            ]
          : [COLOR_QUALITY.GREEN, COLOR_QUALITY.GREEN, COLOR_QUALITY.GREEN]
        : isCompleted
          ? [
              COLOR_QUALITY.GREY_DARK,
              COLOR_QUALITY.GREY_DARK,
              COLOR_QUALITY.GREY_DARK,
            ]
          : [COLOR_QUALITY.RED, COLOR_QUALITY.RED, COLOR_QUALITY.RED];
    case "z":
      return [COLOR_QUALITY.GREY, COLOR_QUALITY.GREY, COLOR_QUALITY.GREY];
    default:
      return isCompleted
        ? [COLOR_QUALITY.GREY, COLOR_QUALITY.GREY, COLOR_QUALITY.GREY]
        : [COLOR_QUALITY.RED, COLOR_QUALITY.GREY, COLOR_QUALITY.GREY];
  }
};

export const addCurrentItemToChild = (data: IAssessmentDetail) => {
  const { checklistItem } = data;
  return {
    ...data,
    checklistItem: checklistItem || [],
  };
};

export const getAltName = (altName: string) => {
  const subString = altName.split(" ");
  if (subString.length > 1) {
    return subString[0].charAt(0) + subString[1].charAt(0);
  }
  return subString[0].charAt(0);
};

export const toFixed = (num: number, digit = 2) => {
  if (num === 0) {
    return 0;
  }
  let output: any = (num || 0).toFixed(digit);
  while (
    output[output.length - 1] === "0" ||
    output[output.length - 1] === "."
  ) {
    output = output.slice(0, -1);
  }
  output = Number(output);
  return Math.round(output);
};

export const searchKeyValue = (menuData, keyValue) => {
  const results: any = [];

  const recursiveSearch = (items) => {
    items.forEach((item) => {
      if (
        (item.title &&
          item.title.toLowerCase().includes(keyValue.toLowerCase())) ||
        (item.description &&
          item.description.toLowerCase().includes(keyValue.toLowerCase())) ||
        (item.descriptionInSearchModal &&
          item.descriptionInSearchModal
            .toLowerCase()
            .includes(keyValue.toLowerCase()))
      ) {
        results.push({
          title: item.title,
          path: item.url,
          description: item.descriptionInSearchModal,
        });
      }

      if (item.children) {
        recursiveSearch(item.children);
      }
    });
  };

  menuData.forEach((menuGroup) => recursiveSearch(menuGroup));

  return results;
};

export const handleSvg = async (file) => {
  const svgText = await file.text();

  // Parse the SVG and create a DOM element
  const parser = new DOMParser();
  const svgDoc = parser.parseFromString(svgText, "image/svg+xml");
  const svgElement = svgDoc.documentElement;

  // Get the dimensions from width/height attributes or viewBox
  let width, height;
  const svgWidth = svgElement.getAttribute("width");
  if (svgWidth) width = parseFloat(svgWidth);
  const svgHeight = svgElement.getAttribute("height");
  if (svgHeight) height = parseFloat(svgHeight);

  // If width and height are not set, use viewBox dimensions
  if (isNaN(width) || isNaN(height)) {
    const viewBox = svgElement.getAttribute("viewBox");
    if (viewBox) {
      const viewBoxValues = viewBox.split(" ").map(parseFloat);
      if (viewBoxValues.length === 4) {
        width = viewBoxValues[2];
        height = viewBoxValues[3];
      }
    }
  }

  if (!isNaN(width) && !isNaN(height)) {
    if (width > height) {
      // Scale based on width if it's the larger dimension
      svgElement.setAttribute("width", "600");
      svgElement.setAttribute("height", ((600 * height) / width).toString());
    } else {
      // Scale based on height if it's the larger dimension
      svgElement.setAttribute("height", "600");
      svgElement.setAttribute("width", ((600 * width) / height).toString());
    }

    const serializedSvg = new XMLSerializer().serializeToString(svgElement);
    return new File([serializedSvg], file.name, {
      type: "image/svg+xml",
    });
  } else {
    console.error("Could not determine SVG dimensions.");
  }
};

export const checkParentIndex = (currentRoute, menuItems, primaryMenuIndex) => {
  let parentIndex;
  menuItems.forEach((items, index) => {
    items.forEach((item) => {
      if (item.url === currentRoute) {
        parentIndex = index;
      }
      if (item.children) {
        item.children.forEach((child) => {
          if (currentRoute === child.url) {
            parentIndex = index;
          }
        });
      }
    });
  });
  return parentIndex === 0 || parentIndex ? parentIndex : primaryMenuIndex;
};

export const generateSelectedItems = (
  items: IMultiSelectDropdown[],
  parentTitle = "",
) => {
  return items.reduce((result: IMultiSelectDropdown[], item) => {
    if (item.selected) result.push({ ...item, parentTitle });

    if (item.childData) {
      const childItems = generateSelectedItems(item.childData, item.title);
      result.push(...childItems);
    }

    return result;
  }, []);
};

export const removeSteps = (listSrepRemove, checkListGroupId, checklistId) => {
  try {
    const listFilterSrepRemove = listSrepRemove.filter(
      (item) => item?.checklistGroupActionId,
    );
    const promises = listFilterSrepRemove.map((item) => {
      const data = {
        checklistGroupActionIsArchived: true,
      };
      return remoOrEditveStep(
        checklistId,
        checkListGroupId,
        item.checklistGroupActionId,
        data,
      );
    });
    return promises;
  } catch (error) {
    console.log("error", error);
  }
};

export const editSteps = (listSteps, checkListGroupId, checklistId) => {
  try {
    const listFilterSrepRemove = listSteps.filter(
      (item) => item?.checklistGroupActionId,
    );
    const promises = listFilterSrepRemove.map((item) => {
      const data = {
        checklistGroupActionHtml: item.checklistGroupActionHtml,
        checklistGroupActionName: item.checklistGroupActionName,
        checklistGroupActionOrder: item.checklistGroupActionOrder,
        checklistGroupActionIsArchived: item.checklistGroupActionIsArchived,
      };
      return remoOrEditveStep(
        checklistId,
        checkListGroupId,
        item.checklistGroupActionId,
        data,
      );
    });
    return promises;
  } catch (error) {
    console.log("error", error);
  }
};
export const getChecklistSelected = (
  data,
  currentChecklistReportId,
  isTopLevelOnly,
) => {
  let selectedChecklist;

  data.forEach((item: any) => {
    if (item.checklistId === currentChecklistReportId) {
      selectedChecklist = item;
    } else {
      item.checklistParent?.forEach((child: any) => {
        if (child.checklistId === currentChecklistReportId) {
          if (!isTopLevelOnly) {
            selectedChecklist = child;
          } else {
            selectedChecklist = item;
          }
        }
      });
    }
  });
  if (selectedChecklist) {
    selectedChecklist.checklistDate = selectedChecklist.checklistDateReporting
      ? selectedChecklist.checklistDateReporting
      : selectedChecklist.checklistDateCreated;
  }

  return selectedChecklist;
};

export const resultFilter = (value: string, data, isTopLevelOnly) => {
  const resultFilter = data.map((item: any) => {
    let result, child;
    if (
      item.checklistName.toLowerCase().includes(value.toLowerCase()) ||
      convertUTCTimeToLocalTime(item?.checklistDateCreated)
        .toLowerCase()
        .includes(value.toLowerCase()) ||
      item.checklistStatus.toLowerCase().includes(value.toLowerCase())
    ) {
      result = { ...item, checklistParent: null };
    }
    if (
      item.checklistParent &&
      item.checklistParent.length &&
      !isTopLevelOnly
    ) {
      child = item.checklistParent.filter((child: any) => {
        return (
          child.checklistName.toLowerCase().includes(value.toLowerCase()) ||
          convertUTCTimeToLocalTime(child?.checklistDateCreated)
            .toLowerCase()
            .includes(value.toLowerCase()) ||
          child.checklistStatus.toLowerCase().includes(value.toLowerCase())
        );
      });
    }
    if (child && child.length > 0 && !result) {
      return {
        ...{ ...item, checklistParent: null },
        isHidden: true,
        checklistParent: child,
      };
    }
    if (child && child.length > 0 && result) {
      return { ...result, checklistParent: child };
    }
    if (result) return result;
  });

  return resultFilter.filter((item: any) => item);
};

export const parseDescription = (checklistGroup) => {
  const checklistGroupDescription =
    checklistGroup?.checklistGroupDescription || "";
  let img = "",
    description = checklistGroupDescription;
  try {
    const descriptions = JSON.parse(checklistGroupDescription);
    if (descriptions?.image) {
      const startChar = descriptions?.image.slice(0, 2);
      img = `data:image/${
        startChar !== "iV" ? "svg+xml" : "png"
      };base64,${descriptions?.image}`;
    }
    description = descriptions?.description;
    return { checklistGroupImage: img, checklistGroupDescription: description };
  } catch (e) {
    return { checklistGroupImage: img, checklistGroupDescription: description };
  }
};

export const isContainHTML = (str) => {
  const doc = new DOMParser().parseFromString(str, "text/html");
  return Array.from(doc.body.childNodes).some((node) => node.nodeType === 1);
};

export const hasValue = (value) => {
  return value !== null && value !== undefined && value !== "";
};

export const ensurePeriod = (text) => {
  if (!text) return;
  const trimmed = text.trimEnd();

  if (trimmed.endsWith(".")) {
    return trimmed;
  } else {
    return trimmed + ".";
  }
};
export default {
  IsEmailValid,
  IsDomainNameValid,
  IsDomainNameValidCustom,
  getQuestionsMatchedListKey,
  getLabelCustom,
  getListDataKeyAtLevel,
  dropDownFitContent,
  getUiText,
  getProcessedUIText,
  getV3Recaptcha,
  getUniqueID,
  sanitizeHTML,
  getComponentUiText,
  getShortChecklistName,
  getFirstAndLastNameFromName,
  getDomainMessage,
  getProcessedRoute,
  searchFilter,
  base64toBlob,
  base64ToFile,
  getFullnamFromFirstAndLastName,
  afterLoginActions,
  IsSpecialCharEmailValid,
  IsSpecialCharDomainValid,
  regexMatch,
  IsSpecialCharNameValid,
  isEnableMFA,
  formatBytes,
  getTextWidth,
  convertDataFlatToTree,
  downloadBase64File,
  encode,
  decode,
  removeNullProperties,
  isUrlFacingWeb,
  optionDay,
  passwordHash,
  getDomainFromUrl,
  mergeScanIssuesCounts,
  mergeScanCounts,
  calculateTotalIssues,
  fillterAll,
  resultData,
  calculatePercentile,
  getTagRelativeRisk,
  getDefaultBackgroundColor,
  downloadCSV,
  getISOLanguageCode,
  getImageData,
  processingRiskValue,
  convertRiskValue,
  countElementsFromTree,
  convertQuality,
  checkIsHavePermisstion,
  countLeafNodes,
  getProgressAnimation,
  getParent,
  formatVendorData,
  searchQuestion,
  getNextDay,
  isContainHTML,
  hasValue,
  getDifferences,
};
